import API from "../../services/api";

async function getFirstOk(paths, params) {
  let lastErr;
  for (const url of paths) {
    try {
      const res = await API.get(url, { params });
      return res.data;
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      if (status === 404 || status === 405 || status === 422) continue;
      throw e;
    }
  }
  throw lastErr || new Error("Request failed");
}

async function postAttempts(attempts) {
  let lastErr;
  for (const { url, body } of attempts) {
    try {
      const res = await API.post(url, body);
      return res.data;
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      if (status === 404 || status === 405) continue;
      throw e;
    }
  }
  throw lastErr || new Error("Request failed");
}

/** Fallback when bulk allocation routes are missing on an older backend build. */
async function bulkAssignViaEmployeeShifts({ template_id, employee_ids, effective_from, effective_to, reason }) {
  const created_assignment_ids = [];
  const failed = [];
  for (const employee_id of employee_ids) {
    try {
      const res = await API.post("/wf/employee-shifts", {
        employee_id,
        template_id,
        effective_from,
        effective_to: effective_to || undefined,
        reason,
      });
      const id = res.data?.assignment_id;
      if (id) created_assignment_ids.push(String(id));
    } catch (e) {
      const d = e?.response?.data?.detail;
      failed.push({
        employee_id: String(employee_id),
        code: typeof d === "object" ? d.code : undefined,
        message:
          (typeof d === "object" && d.message) ||
          (typeof d === "string" ? d : e.message || "Assign failed"),
      });
    }
  }
  return {
    created_count: created_assignment_ids.length,
    created_assignment_ids,
    failed,
  };
}

export async function fetchShiftOperationsSummary(params = {}) {
  return getFirstOk(
    [
      "/wf/employee-shift-allocations/summary",
      "/wf/shift-operations/summary",
      "/wf/shift-templates/operations/summary",
    ],
    params
  );
}

export async function fetchShiftAllocations(params = {}) {
  return getFirstOk([
    "/wf/employee-shift-allocations",
    "/wf/shift-allocations",
    "/wf/shift-templates/allocations",
  ], params);
}

export async function fetchShiftTemplateEmployees(templateId, params = {}) {
  const res = await API.get(`/wf/shift-templates/${templateId}/employees`, { params });
  return res.data;
}

export async function assignShiftAllocation(data) {
  const { template_id, employee_id, effective_from, effective_to, reason } = data;
  const body = {
    employee_ids: [employee_id],
    effective_from,
    effective_to: effective_to || undefined,
    reason,
  };
  return postAttempts([
    { url: "/wf/employee-shifts", body: data },
    { url: `/wf/employee-shift-allocations/by-template/${template_id}`, body },
    { url: `/wf/shift-templates/${template_id}/employees`, body },
    { url: "/wf/employee-shift-allocations/assign", body: data },
    { url: "/wf/shift-templates/allocations/assign", body: data },
  ]);
}

export async function bulkShiftAllocation(data) {
  const { template_id, employee_ids, effective_from, effective_to, reason } = data;
  const body = {
    employee_ids,
    effective_from,
    effective_to: effective_to || undefined,
    reason,
  };
  try {
    return await postAttempts([
      { url: `/wf/employee-shift-allocations/by-template/${template_id}`, body },
      { url: `/wf/shift-templates/${template_id}/employees`, body },
      { url: "/wf/employee-shift-allocations/bulk", body: data },
      { url: "/wf/shift-allocations/bulk", body: data },
      { url: "/wf/shift-templates/allocations/bulk", body: data },
    ]);
  } catch (e) {
    const status = e?.response?.status;
    if (status === 404 || status === 405) {
      return bulkAssignViaEmployeeShifts(data);
    }
    throw e;
  }
}

export async function changeShiftAllocation(data) {
  return postAttempts([
    { url: "/wf/shift-allocations/change", body: data },
    { url: "/wf/shift-templates/allocations/change", body: data },
  ]);
}

export async function endShiftAllocation(assignmentId, data = {}) {
  return postAttempts([
    { url: `/wf/shift-allocations/${assignmentId}/end`, body: data },
    { url: `/wf/shift-templates/allocations/${assignmentId}/end`, body: data },
  ]);
}
